import React, { useEffect, useMemo, useState } from 'react'
import { Card } from 'primereact/card';
import { Icon } from '@iconify/react';
import { useUser } from '../contexts/UserContext'
import { useAuth } from '../contexts/AuthContext';
import { Toast } from 'primereact/toast';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { userNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddUser from '../components/AddUser';
import CSelect from '../components/CSelect';
import PageHeader from '../components/PageHeader';
import InfiniteScroll from 'react-infinite-scroll-component';

const roleOptionsList = [
  { label: 'Admin', value: 'admin' },
  { label: 'Staff', value: 'staff' },
  { label: 'All', value: 'all' }
];

function UserList() {
  const { toast, auth, restaurantId, hasPermission } = useAuth();
  const { getAllUsers, getSingleUser } = useUser()

  const canCreate = useMemo(() => hasPermission("users", "create"), [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("users", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("users", "delete"), [hasPermission]);
  const canVisibleHourlyRate = useMemo(() => hasPermission("is_hourly_rate_visible", "access"), [hasPermission]);

  const [userList, setUserList] = useState([])
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState({
    role: 'all',
    restaurant_id: null,
    limit: 50,
    totalRenderedRecords: 0
  })
  const [credential, setCredential] = useState({
    user_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    user_group_id: "",
    telephone: "",
    pin: "",
    access: "",
    GSM: "",
    street: "",
    street_number: "",
    role: "",
    city: "",
    zip: "",
    floor: "",
    table: "",
    is_super_admin: 0,
    rate_per_hour: "",
    image: "",
    restaurantIds: [],
  })

  useEffect(() => {
    if (filter) {
      fetchUsers()
    }
  }, [filter])

  useEffect(() => {
    if (restaurantId) {
      handleFilterChange({ ...filter, restaurant_id: restaurantId })
    }
  }, [restaurantId])

  const fetchUsers = async () => {
    if (restaurantId) {
      setIsLoading(true);

      const data = await getAllUsers({ ...filter, restaurant_id: restaurantId });
      if (data.users.length !== 0 && data.totalCount !== userList.length) {
        setUserList([...userList, ...data.users]);
        setTotalRecords(data.totalCount);
        setTotalRenderedRecords(userList.length);
      }

      setIsLoading(false);
    }
  };

  const handleCardClick = (user) => {
    if (!canUpdate && !canDelete) return;
    setEditMode(true);
    setEditUserId(user.id);
    setVisible(false);
  };

  const handleFilterChange = async (newFilter) => {
    setUserList([]);
    setFilter({ ...newFilter, totalRenderedRecords: 0, });
    setHasMore(true);
  };

  const handleLoadMore = () => {
    if (userList.length === totalRecords) {
      setHasMore(false);
    } else if (userList.length === filter.limit) {
      const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

      setFilter(updatedFilter);
      setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
    } else {
      const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

      setFilter(updatedFilter);
      setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
    }
  };

  const fetchUserProps = async (newUserId) => {
    try {
      if (editMode && editUserId) {
        const user = await getSingleUser(editUserId);
        const userIndex = userList.findIndex((u) => u.id === editUserId);
        const isUserInSelectedRestaurant = user?.restaurantIds?.some((restaurant) => restaurant?.value === restaurantId);

        if (userIndex !== -1) {
          if (!user || !isUserInSelectedRestaurant) {
            const updatedUserList = [...userList];
            updatedUserList.splice(userIndex, 1);

            setUserList(updatedUserList);
            setTotalRecords(totalRecords - 1);
            setTotalRenderedRecords(totalRenderedRecords - 1)
          } else {
            const updatedUserList = [...userList];
            updatedUserList[userIndex] = user;

            setUserList(updatedUserList);
          }
        } else if (user.is_super_admin === 0) {
          setUserList([...userList, user]);
        }
      } else if (visible) {
        const updatedUserList = [...userList];
        updatedUserList.unshift({ ...credential, id: newUserId });
        setUserList(updatedUserList);
        setTotalRecords(totalRecords + 1);
        setTotalRenderedRecords(totalRenderedRecords + 1)
      }
    } catch (error) {
      console.error('Error fetching users.', error);
    }
  };

  const renderCardsInRows = () => {
    const rows = [];
    let currentRow = [];
    let count = 0;

    userList && userList.forEach((user, index) => {
      currentRow.push(
        <div key={user.id} className="d-flex">
          <Card
            title={
              <div className='product-name' title={user.user_name}>
                #{user.id} - {user.user_name}
              </div>
            }
            header={user.image ?
              <LazyLoadImage
                alt={user.user_name}
                src={user.image}
                className='rounded-img'
              />
              :
              <Icon icon="mdi:user" className='card-icon' height={100} width={100} />}
            className={`user-list-card ${user.is_active && 'active_user'}`}
            onClick={() => handleCardClick(user)}
          />
        </div>
      );

      count++;
      rows.push(
        <div key={index} className="d-flex">
          {currentRow}
        </div>
      );
      currentRow = [];
      count = 0;
    });

    return rows;
  };

  return (
    <Layout2 items={userNav.items} navTitle={userNav.header}>
      <div className="d-flex flex-column w-100">
        <PageHeader title={'Users'} />
        <div className='d-flex flex-row px-4 py-2 w-100 user-list-nav'>
          <div>
            <CSelect
              label="Show"
              className='d-flex'
              labelClassName='me-2 pt-1'
              value={filter.role}
              onChange={(value) => handleFilterChange({ ...filter, role: value })}
              options={roleOptionsList}
            />
          </div>
        </div>
        <div className='px-4 py-2'>
          {
            (visible || (editMode && editUserId)) &&
            (
              <AddUser
                id={editUserId}
                visible={visible}
                editUserId={editUserId}
                editMode={editMode}
                credential={credential}
                setEditUserId={setEditUserId}
                setVisible={setVisible}
                setEditMode={setEditMode}
                fetchUsers={fetchUserProps}
                setCredential={setCredential}
                canCreate={canCreate}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canVisibleHourlyRate={canVisibleHourlyRate}
              />
            )
          }
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <Toast ref={toast} />
              <div className='user-list'>
                <InfiniteScroll
                  dataLength={userList.length}
                  next={handleLoadMore}
                  hasMore={hasMore}
                  className="card-container"
                >
                  {
                    canCreate && <Card
                      title="Add User"
                      header={<Icon icon="tabler:plus" style={{ color: "#888888" }} height={70} width={70} />}
                      className="add-product-card"
                      onClick={() => setVisible(true)}
                    />
                  }
                  {
                    (auth.user && auth.user.is_super_admin === 1) && (
                      <Card
                        title={
                          <div className='product-name' title={auth.user.user_name}>
                            #{auth.user.id} - {auth.user.user_name}
                          </div>
                        }
                        header={auth.user.image ?
                          <LazyLoadImage
                            alt={auth.user.user_name}
                            src={auth.user.image}
                            className='rounded-img'
                          />
                          :
                          <Icon icon="mdi:user" className='card-icon' height={100} width={100} />}
                        className={`user-list-card ${auth.user.is_active && 'active_user'}`}
                        onClick={() => handleCardClick(auth.user)}
                      />
                    )
                  }
                  {userList.length !== 0 && (
                    renderCardsInRows()
                  )}
                </InfiniteScroll>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout2>
  )
}

export default UserList